import { EventTypes, InterceptorTypes, Player } from '@dicetechnology/doris-core';
import { ChromecastReceiverPluginEvents, isChromecastReceiverPlugin } from '@dicetechnology/doris-plugin-chromecast-receiver';
import { PlayerContainer } from '@dicetechnology/doris-ui';
import type { cast as castNs } from 'chromecast-caf-receiver';
import { useEffect, useRef, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';

import { PlayerInterface } from './components';
import type { DiceReceiverError } from './errors';
import { DiceReceiverManager, DiceReceiverManagerEvents } from './services';
import type { Beacon } from './services/Beacon';
import { buildPlayerConfiguration, configureHandlerPreloading } from './utils';

declare const cast: typeof castNs;

export const App = () => {
    const rootRef = useRef<HTMLDivElement>(null);
    const [player, setPlayer] = useState<Player>();
    const { showBoundary } = useErrorBoundary();

    const showErrorBoundaryRef = useRef(showBoundary);
    showErrorBoundaryRef.current = showBoundary;

    useEffect(() => {
        if (!rootRef.current) {
            console.error('Player root not created!');
            return;
        }

        const diceReceiverManager = DiceReceiverManager.getInstance();
        const player = new Player(rootRef.current, buildPlayerConfiguration());
        configureHandlerPreloading(player);

        setPlayer(player);

        let beacon: Beacon | undefined;

        const handleConfigured = () => {
            beacon = diceReceiverManager.getBeacon();
        };

        const handleError = (error: DiceReceiverError) => {
            showErrorBoundaryRef.current(error);
        };

        const cleanup = () => {
            beacon?.stop(true);
            beacon = undefined;
        };

        diceReceiverManager
            .addListener(DiceReceiverManagerEvents.CONFIGURED, handleConfigured)
            .addListener(DiceReceiverManagerEvents.ERROR, handleError);

        player
            .createSubscriptionContext()
            .on(EventTypes.READY, () => {
                beacon?.start();
            })
            .on(EventTypes.TIME_UPDATE, (_, { currentTime }) => {
                if (!beacon) {
                    return;
                }
                const { interceptor } = player.getServices();
                const contentTime = interceptor.runInterceptors(InterceptorTypes.STREAM_TIME_TO_CONTENT_TIME, currentTime);
                beacon.updateProgress(contentTime);
            })
            .on(EventTypes.DESTROYING, () => {
                cleanup();
            });

        player.findOrWaitForPlugin(isChromecastReceiverPlugin).then((receiver) => {
            receiver.on(ChromecastReceiverPluginEvents.CAST_FRAMEWORK_READY, () => {
                cast.framework.CastReceiverContext.getInstance().addEventListener(cast.framework.system.EventType.SHUTDOWN, cleanup);
            });
        });

        return () => {
            diceReceiverManager.removeListener(DiceReceiverManagerEvents.CONFIGURED, handleConfigured);
            diceReceiverManager.removeListener(DiceReceiverManagerEvents.ERROR, handleError);

            player.destroy();
            setPlayer(undefined);
        };
    }, []);

    return (
        <>
            <div ref={rootRef}></div>
            {player && (
                <PlayerContainer player={player}>
                    <PlayerInterface />
                </PlayerContainer>
            )}
        </>
    );
};
